import { useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { Button, Field, Modal, TextareaField } from "@fawkes/ui";

import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";

/**
 * Het onderdeel Voeding: wat het dier krijgt, en een potlood om het schema te wijzigen.
 *
 * <p>De eerste van de potlood-kant, en daarmee het patroon voor de rest ervan. Waar gewicht een
 * <em>registratie</em> toevoegt aan de historie, overschrijft dit <em>vaste informatie</em>: een
 * voedingsschema is hoe het nú hoort, niet wat er gisteren gebeurde. Wie het wijzigt verandert de
 * instructie voor iedereen die daarna voert. Dat verschil zit in de knop -- plus tegen potlood -- en
 * staat in de componentbibliotheek, niet hier.
 *
 * <p><b>Twee bronnen voor één regel.</b> Staat er een schema, dan zegt dat wat het dier krijgt. Staat er
 * geen schema, dan is de voedingscategorie van het dier ("Biks", "Renal") nog steeds het enige antwoord
 * op die vraag, en die komt in dezelfde bevraging mee. Zonder die terugval zou de tegel "nog niets"
 * zeggen over een dier waarvan wél bekend is wat het eet -- en dat is precies het soort leegte waar een
 * verzorger de verkeerde conclusie uit trekt.
 *
 * <p>Een voermoment bijschrijven is iets anders en hoort bij de voerlijst; dat mag iedereen, het schema
 * wijzigen niet.
 */

export interface Plan {
  id: number | null;
  version: number | null;
  feedType: string | null;
  brand: string | null;
  amountText: string | null;
  feedingTimes: string | null;
  note: string | null;
}

interface VoedingStand {
  voedingCategorie: string | null;
  plan: Plan | null;
}

export function VoedingOnderdeel({ dierId }: { dierId: number }) {
  const t = useTranslate();
  const { data: rechten } = usePermissions<string[]>();
  // Het benodigde recht staat in de componentbibliotheek, niet hier: anders krijgt hetzelfde onderdeel
  // in de tegelbouwer een andere grens dan op de tegel zelf.
  const def = COMPONENT_PER_SLEUTEL.voeding;
  const magWijzigen = !def.recht || (rechten ?? []).includes(def.recht);

  const { data, refetch } = useCustom<VoedingStand>({
    url: `animals/${dierId}/feeding`,
    method: "get",
  });
  const stand = data?.data;
  const plan = stand?.plan ?? null;
  const [open, setOpen] = useState(false);

  // Merk en soort samen, want los zeggen ze weinig: "Royal Canin" zonder "brok" of andersom laat de
  // verzorger raden.
  const uitSchema = [plan?.brand, plan?.feedType].filter(Boolean).join(" ");
  const waarde = uitSchema || stand?.voedingCategorie || null;
  const hoeveelheid = [plan?.amountText, plan?.feedingTimes].filter(Boolean).join(" · ");

  return (
    <>
      <DierOnderdeel
        def={def}
        waarde={waarde}
        // Zonder schema staat er alleen een categorie; zeggen dat het schema mist is eerlijker dan een
        // lege tweede regel.
        herkomst={hoeveelheid || (waarde && !uitSchema ? t("dierTegel.voeding.geenSchema") : undefined)}
        magSchrijven={magWijzigen}
        onActie={() => setOpen(true)}
      >
        {plan?.note ? <span className="do-onderdeel__herkomst">{plan.note}</span> : null}
      </DierOnderdeel>
      {open ? (
        <VoedingWijzigen
          dierId={dierId}
          plan={plan}
          onSluit={() => setOpen(false)}
          onOpgeslagen={() => { setOpen(false); refetch?.(); }}
        />
      ) : null}
    </>
  );
}

/**
 * Het voedingsschema wijzigen.
 *
 * <p>Het hele schema gaat mee en niet alleen het gewijzigde veld: de api werkt bij met een upsert, dus
 * een veld dat je niet meestuurt komt als leeg binnen en wist wat er stond. {@code id} en
 * {@code version} reizen mee zodat een schema dat iemand anders intussen wijzigde niet stil wordt
 * overschreven -- de api geeft dan 409 en dit scherm vraagt om opnieuw te openen.
 *
 * <p>Dieetstatus en ingangsdatums staan er niet in, net zomin als in het volledige tabblad: die horen
 * bij de afspraak met de dierenarts en niet bij een snelle correctie vanaf een tegel.
 */
export function VoedingWijzigen({ dierId, plan, onSluit, onOpgeslagen }: {
  dierId: number;
  plan: Plan | null;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [merk, setMerk] = useState(plan?.brand ?? "");
  const [soort, setSoort] = useState(plan?.feedType ?? "");
  const [hoeveelheid, setHoeveelheid] = useState(plan?.amountText ?? "");
  const [tijden, setTijden] = useState(plan?.feedingTimes ?? "");
  const [notitie, setNotitie] = useState(plan?.note ?? "");
  const [fout, setFout] = useState<string | null>(null);

  const leeg = (v: string) => (v.trim() === "" ? null : v.trim());
  // Een schema zonder één gevuld veld is geen schema; opslaan zou een lege rij aanmaken.
  const ietsIngevuld = [merk, soort, hoeveelheid, tijden, notitie].some((v) => v.trim() !== "");

  const bewaar = () => {
    setFout(null);
    mutate(
      {
        url: `animals/${dierId}/feeding/plan`,
        method: "put",
        values: {
          id: plan?.id ?? null,
          version: plan?.version ?? null,
          brand: leeg(merk),
          feedType: leeg(soort),
          amountText: leeg(hoeveelheid),
          feedingTimes: leeg(tijden),
          note: leeg(notitie),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: onOpgeslagen,
        onError: (e: { statusCode?: number }) =>
          setFout(t(e?.statusCode === 409 ? "dierTegel.voeding.conflict" : "dierTegel.voeding.fout")),
      },
    );
  };

  return (
    <Modal open title={t("dierTegel.voeding.wijzigen")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={bewaar} disabled={bezig || !ietsIngevuld}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="do-invoer">
        <Field label={t("dierTegel.voeding.merk")} value={merk} maxLength={255}
          data-testid="voeding-merk" onChange={(e) => setMerk(e.target.value)} />
        <Field label={t("dierTegel.voeding.soort")} value={soort} maxLength={150}
          onChange={(e) => setSoort(e.target.value)} />
        <Field label={t("dierTegel.voeding.hoeveelheid")} value={hoeveelheid} maxLength={150}
          onChange={(e) => setHoeveelheid(e.target.value)} />
        <Field label={t("dierTegel.voeding.tijden")} value={tijden} maxLength={150}
          onChange={(e) => setTijden(e.target.value)} />
        <TextareaField label={t("dierTegel.voeding.notitie")} value={notitie} rows={2}
          onChange={(e) => setNotitie(e.target.value)} />
        <p className="do-invoer__hint">{t("dierTegel.voeding.hint")}</p>
        {fout ? <p className="do-invoer__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}
